import { Sparkline } from './Sparkline';
import { PeriodBadge } from './PeriodBadge';
import type { SemSummary } from '@/types/sem';
import type { DailySnapshotRow, PeriodComparison } from '@/lib/supabase-to-sem';
import { comparePeriods } from '@/lib/supabase-to-sem';

interface PortfolioPulseProps {
  summary: SemSummary;
  dailySnapshots: DailySnapshotRow[];
  prevSnapshots: DailySnapshotRow[];
}

function formatCurrency(value: number, decimals = 0): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(value);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(Math.round(value));
}

interface MetricCardProps {
  label: string;
  value: string;
  comparison: PeriodComparison;
  sparkData: number[];
  sparkColor?: string;
  invertColor?: boolean;
  valueColor?: string;
}

function MetricCard({ label, value, comparison, sparkData, sparkColor = 'rgba(255,255,255,0.3)', invertColor, valueColor }: MetricCardProps) {
  return (
    <div className="ed-card p-4">
      <div className="text-[0.65rem] text-[var(--ed-t3)] mb-1 font-medium uppercase tracking-wider">{label}</div>
      <div className={`text-2xl font-display font-light tracking-tight ${valueColor || 'text-foreground'}`}>{value}</div>
      <div className="flex items-center gap-2 mt-1.5">
        <PeriodBadge
          percentChange={comparison.percentChange}
          direction={comparison.direction}
          invertColor={invertColor}
          label="vs prev"
        />
      </div>
      <div className="mt-2">
        <Sparkline data={sparkData} color={sparkColor} />
      </div>
    </div>
  );
}

export function PortfolioPulse({ summary, dailySnapshots, prevSnapshots }: PortfolioPulseProps) {
  // Aggregate daily values for sparklines
  const dailyByDate = new Map<string, DailySnapshotRow[]>();
  for (const s of dailySnapshots) {
    const existing = dailyByDate.get(s.date) || [];
    existing.push(s);
    dailyByDate.set(s.date, existing);
  }
  const sortedDates = Array.from(dailyByDate.keys()).sort();

  const spendByDay = sortedDates.map(d => dailyByDate.get(d)!.reduce((s, r) => s + r.spend, 0));
  const convsByDay = sortedDates.map(d => dailyByDate.get(d)!.reduce((s, r) => s + r.conversions, 0));
  const roasByDay = sortedDates.map(d => {
    const rows = dailyByDate.get(d)!;
    const spend = rows.reduce((s, r) => s + r.spend, 0);
    const value = rows.reduce((s, r) => s + r.conversion_value, 0);
    return spend > 0 ? value / spend : 0;
  });
  const cpaByDay = sortedDates.map(d => {
    const rows = dailyByDate.get(d)!;
    const spend = rows.reduce((s, r) => s + r.spend, 0);
    const convs = rows.reduce((s, r) => s + r.conversions, 0);
    return convs > 0 ? spend / convs : 0;
  });
  const qsByDay = sortedDates.map(d => {
    const rows = dailyByDate.get(d)!;
    return rows.reduce((s, r) => s + r.quality_score_avg, 0) / rows.length;
  });

  const spendComparison = comparePeriods(dailySnapshots, prevSnapshots, 'spend');
  const convsComparison = comparePeriods(dailySnapshots, prevSnapshots, 'conversions');
  const roasComparison = comparePeriods(dailySnapshots, prevSnapshots, 'roas');
  const cpaComparison = comparePeriods(dailySnapshots, prevSnapshots, 'cpa');
  const qsComparison = comparePeriods(dailySnapshots, prevSnapshots, 'quality_score_avg');

  return (
    <div className="space-y-4">
      <div className="ed-section-label">
        Google Ads Performance
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
        <MetricCard
          label="Total Spend"
          value={formatCurrency(summary.spend)}
          comparison={spendComparison}
          sparkData={spendByDay}
        />
        <MetricCard
          label="Conversions"
          value={formatNumber(summary.conversions)}
          comparison={convsComparison}
          sparkData={convsByDay}
        />
        <MetricCard
          label="ROAS"
          value={`${summary.roas.toFixed(2)}x`}
          comparison={roasComparison}
          sparkData={roasByDay}
          sparkColor="#4aba7a"
          valueColor="text-[var(--ed-green)]"
        />
        <MetricCard
          label="CPA"
          value={formatCurrency(summary.cpa, 2)}
          comparison={cpaComparison}
          sparkData={cpaByDay}
          invertColor
        />
        <MetricCard
          label="Avg Quality Score"
          value={`${Math.round(summary.qualityScoreAvg)}/10`}
          comparison={qsComparison}
          sparkData={qsByDay}
          sparkColor="#e0a852"
        />
      </div>
    </div>
  );
}
